import { useState, useEffect, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { Search, Filter, X, Loader2 } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { PostCard } from "@/components/posts/PostCard"
import { Sidebar } from "@/components/layout/Sidebar"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { searchPosts, getPostsWithAuthors, enrichPostsWithTags } from "@/lib/db"
import type { PostWithAuthor } from "@/types"

export default function SearchPage() {
  const [searchParams] = useSearchParams()
  const initialQuery = searchParams.get("q") || ""
  const [query, setQuery] = useState(initialQuery)
  const [sortBy, setSortBy] = useState("relevance")
  const [results, setResults] = useState<PostWithAuthor[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([])
      return
    }

    setIsSearching(true)
    try {
      const posts = await searchPosts(searchQuery.trim())
      const withAuthors = await getPostsWithAuthors(posts)
      const enriched = await enrichPostsWithTags(withAuthors)
      setResults(enriched)
    } catch (error) {
      console.error("Search failed:", error)
      setResults([])
    } finally {
      setIsSearching(false)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query)
    }, 300)
    return () => clearTimeout(timer)
  }, [query, performSearch])

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="mb-4 text-3xl font-bold text-gray-900 dark:text-white">Search</h1>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="search"
              placeholder="Search posts, tags, authors..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="pl-10"
            />
            {query && (
              <Button variant="ghost" size="sm" className="absolute right-1 top-1/2 -translate-y-1/2" onClick={() => setQuery("")}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full sm:w-40">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Relevance</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="popular">Most Popular</SelectItem>
              <SelectItem value="discussed">Most Discussed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col gap-8 lg:flex-row">
        <div className="flex-1 space-y-6">
          {isSearching ? (
            <div className="py-12 text-center">
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-gray-400" />
              <p className="mt-4 text-gray-500 dark:text-gray-400">Searching...</p>
            </div>
          ) : query ? (
            results.length > 0 ? (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Found {results.length} {results.length === 1 ? "result" : "results"} for &quot;{query}&quot;
                </p>
                {results.map(post => (
                  <PostCard key={post.id} post={post} />
                ))}
              </>
            ) : (
              <div className="py-12 text-center">
                <Search className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No results found</h3>
                <p className="mt-2 text-gray-500 dark:text-gray-400">Try different keywords or check your spelling.</p>
              </div>
            )
          ) : (
            <div className="py-12 text-center">
              <Search className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">Start searching</h3>
              <p className="mt-2 text-gray-500 dark:text-gray-400">Enter a search term to find posts, tags, and authors.</p>
            </div>
          )}
        </div>
        <aside className="w-full lg:w-80">
          <Sidebar />
        </aside>
      </div>
    </div>
  )
}
